/**
 * Generic transaction flow hook that encapsulates the shared
 * Validate → Build → Sign → Confirm state machine.
 *
 * Each concrete transaction hook (stake, unstake) provides
 * only a `buildTransaction` callback — the rest is handled here.
 */

import type { BigDecimal } from "@solomon-labs/bigdecimal";
import type { PublicKey } from "@solomon-labs/publickey";
import { ConnectionManager } from "@solomon-labs/solana-connection";
import type { SolanaBlockhash, SolanaTransactionSignature } from "@solomon-labs/types";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";

import { TransactionError } from "../errors/TransactionError";
import type { TransactionState, TransactionStep } from "../types/transaction";
import { INITIAL_TRANSACTION_STATE } from "../types/transaction";
import { notifyTransaction, type TransactionObserver } from "../types/transactionObserver";
import { isNegative, isZero } from "../utils/bigdecimal";
import { confirmTransaction } from "../utils/confirmTransaction";
import { useTransactionSigner } from "../wallet";
import { useWallet } from "../wallet/hooks/useWallet";

/**
 * Context provided to the buildTransaction callback.
 * Contains everything the SDK call needs that is NOT specific to
 * the particular transaction type.
 */
export interface TransactionBuildContext {
    /** The connected wallet's public key */
    publicKey: PublicKey;
    /** Recent blockhash for the transaction */
    recentBlockhash: SolanaBlockhash;
    /** Block height for confirmation expiry */
    lastValidBlockHeight: number;
}

/**
 * Configuration for a specific transaction flow.
 * TArgs is the tuple of arguments the execute function accepts.
 * The first element must always be a BigDecimal (the amount to validate).
 */
export interface TransactionFlowConfig<TArgs extends [BigDecimal, ...unknown[]]> {
    /** Human-readable name for logging (e.g. "useStakeTransaction") */
    name: string;
    observer?: TransactionObserver;
    /**
     * Pure function that builds the transaction.
     * Receives the execute args and build context, returns a serializable transaction.
     */
    buildTransaction: (args: TArgs, context: TransactionBuildContext) => Promise<{ serialize(): Uint8Array }>;
    /** Query keys to invalidate on success */
    invalidateQueryKeys: string[][];
}

/**
 * Return type from useTransactionFlow.
 * Concrete hooks wrap this to expose named execute methods.
 */
export interface TransactionFlowReturn<TArgs extends [BigDecimal, ...unknown[]]> {
    /** Current transaction state */
    state: TransactionState;
    /** Execute the transaction with the given args */
    execute: (...args: TArgs) => Promise<string | null>;
    /** Reset state to initial */
    reset: () => void;
}

/**
 * Generic transaction flow hook.
 *
 * Handles the full Validate → Build → Sign → Confirm lifecycle:
 * - Wallet and signer validation
 * - Amount validation (first arg must be BigDecimal > 0)
 * - Blockhash fetching
 * - Transaction building via config callback
 * - Serialization and signing via ConnectorKit
 * - On-chain confirmation via WebSocket/polling
 * - Error wrapping with TransactionError
 * - Query invalidation on success
 * - Concurrency guard (prevents double-submit)
 *
 * @param config - Transaction-specific configuration
 * @returns Transaction state, execute function, and reset
 */
export function useTransactionFlow<TArgs extends [BigDecimal, ...unknown[]]>(
    config: TransactionFlowConfig<TArgs>
): TransactionFlowReturn<TArgs> {
    const { connected, publicKey } = useWallet();
    const { signer, ready: signerReady } = useTransactionSigner();
    const queryClient = useQueryClient();
    const [state, setState] = useState<TransactionState>(INITIAL_TRANSACTION_STATE);
    const isExecutingRef = useRef(false);

    const execute = useCallback(
        async (...args: TArgs): Promise<string | null> => {
            if (isExecutingRef.current) return null;
            isExecutingRef.current = true;

            let currentStep: TransactionStep = "building";
            let confirmationUnknown = false;
            notifyTransaction(() => config.observer?.stage?.("building"));
            try {
                // === Validation ===
                if (!connected || !publicKey) {
                    const error = TransactionError.validation("WALLET_NOT_CONNECTED", "Wallet not connected");
                    setState({ ...INITIAL_TRANSACTION_STATE, isError: true, errorMessage: error.userMessage });
                    notifyTransaction(() => config.observer?.failed?.(error, false));
                    return null;
                }

                if (!signerReady || !signer) {
                    const error = TransactionError.validation("SIGNER_NOT_READY", "Transaction signer not ready");
                    setState({ ...INITIAL_TRANSACTION_STATE, isError: true, errorMessage: error.userMessage });
                    notifyTransaction(() => config.observer?.failed?.(error, false));
                    return null;
                }

                const amount = args[0];
                if (isZero(amount) || isNegative(amount)) {
                    const error = TransactionError.validation("INVALID_AMOUNT", "Amount must be greater than zero");
                    setState({ ...INITIAL_TRANSACTION_STATE, isError: true, errorMessage: error.userMessage });
                    notifyTransaction(() => config.observer?.failed?.(error, false));
                    return null;
                }

                // === Building ===
                setState({ ...INITIAL_TRANSACTION_STATE, isLoading: true, step: "building" });

                const blockhashResponse = await ConnectionManager.getRpcClient().getLatestBlockhash();
                if (!blockhashResponse) {
                    throw new Error("Failed to get recent blockhash");
                }
                const { blockhash: recentBlockhash, lastValidBlockHeight } = blockhashResponse;

                const transaction = await config.buildTransaction(args, {
                    publicKey,
                    recentBlockhash,
                    lastValidBlockHeight
                });

                // === Signing ===
                currentStep = "signing";
                notifyTransaction(() => config.observer?.stage?.("awaiting_signature"));
                setState((s) => ({ ...s, step: "signing" }));

                const serializedTx = transaction.serialize();
                const signature = await signer.signAndSendTransaction(serializedTx);

                // === Confirming ===
                currentStep = "confirming";
                notifyTransaction(() => config.observer?.submitted?.(signature));
                notifyTransaction(() => config.observer?.stage?.("confirming"));
                setState((s) => ({ ...s, step: "confirming", signature }));

                const result = await confirmTransaction(signature as SolanaTransactionSignature, {
                    lastValidBlockHeight
                });
                if (!result.confirmed) {
                    confirmationUnknown = !/transaction (failed|expired)/i.test(result.error ?? "");
                    throw new Error(result.error || "Transaction failed to confirm");
                }

                // === Success ===
                setState({
                    isLoading: false,
                    isSuccess: true,
                    isError: false,
                    errorMessage: null,
                    signature,
                    step: "complete"
                });

                for (const queryKey of config.invalidateQueryKeys) {
                    void queryClient.invalidateQueries({ queryKey });
                }

                return signature;
            } catch (error) {
                notifyTransaction(() =>
                    config.observer?.failed?.(
                        error,
                        confirmationUnknown ||
                            (currentStep === "signing" &&
                                !/reject|denied|cancel/i.test(error instanceof Error ? error.message : ""))
                    )
                );
                console.error(`[${config.name}] Failed at step "${currentStep}":`, error);
                const txError = TransactionError.fromCaught(error, currentStep);
                setState({
                    isLoading: false,
                    isSuccess: false,
                    isError: true,
                    errorMessage: txError.userMessage,
                    signature: null,
                    step: "idle"
                });
                return null;
            } finally {
                isExecutingRef.current = false;
            }
        },
        [connected, publicKey, signer, signerReady, queryClient, config]
    );

    const reset = useCallback(() => {
        setState(INITIAL_TRANSACTION_STATE);
    }, []);

    return { state, execute, reset };
}
